import React, {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import styles from "./add.module.scss";
import Modal from "../../../../components/modal/Modal";
import {
  TextInput,
  TextInputValue,
} from "../../../../components/text-input/TextInput";
import Button from "../../../../components/button/Button";
import { isEmptyObject, validateIsEmpty } from "../../../../utils/validation";
import { SelectOption, User } from "../../../../types";
import SelectMulti from "../../../../components/select-multi/SelectMulti";
import SelectCreatable from "src/components/select-creatable/SelectCreatable";
import Checkbox from "../../../../components/checkbox/Checkbox";
import { useQueryDictionary } from "../../../../hooks/useQueryDictionary";
import { gql, useMutation } from "@apollo/client";
import { addDays, formatDate } from "../../../../utils/time";
import Select from "../../../../components/select/Select";
import states from "./states.json";
import { ExtendedCustomerFragment as Customer } from "@api/graphql-codegen";
import { useToast } from "@src/hooks/useToast";
import { logger } from "../../../../utils/logger";

type Props = {
  open: boolean;
  onClose: () => void;
  customer?: Customer;
  onCompleted: () => void;
  me?: User;
};

const countriesOptions = [
  { label: "Australia", value: "AU" },
  { label: "Canada", value: "CA" },
  { label: "USA", value: "US" },
];

const emptyErrors = {
  name: "",
  endDate: "",
  country: "",
  state: "",
  city: "",
};

type CreateAccountInput = {
  name: string;
  endDate: number;
  trial: boolean;
  isDemo: boolean;
  country: string;
  state: string;
  city: string;
  disabledPwLogin?: boolean;
  dataCollections: string[];
};

type UpdateAccountInput = {
  id: string;
  name: string;
  endDate: number;
  trial: boolean;
  isDemo: boolean;
  country: string;
  state: string;
  city: string;
  disabledPwLogin: boolean;
  dataCollections: string[];
};

type CreateAccountResponse = {
  addAccount: boolean;
};

type EditAccountResponse = {
  editAccount: boolean;
};

const createMutation = gql`
  mutation CreateAccount($input: AddAccountInput!) {
    addAccount(input: $input)
  }
`;

const editMutation = gql`
  mutation EditAccount($input: EditAccountInput!) {
    editAccount(input: $input)
  }
`;

const today = formatDate(addDays(new Date(), 1).valueOf(), "yyyy-MM-dd");

const states2: { [key: string]: any } = states;

function AddModal(props: Props) {
  const { open, onClose, customer, onCompleted, me } = props;

  const [createAccount, { loading }] = useMutation<
    CreateAccountResponse,
    { input: CreateAccountInput }
  >(createMutation, {
    onError(e) {
      setBeError(e.message);
    },
    onCompleted(response) {
      if (response.addAccount) {
        onCompleted();
        onClose();
        clearFields();
      }
    },
  });

  const [updateAccount, { loading: editLoading }] = useMutation<
    EditAccountResponse,
    { input: UpdateAccountInput }
  >(editMutation, {
    onError(e) {
      setBeError(e.message);
    },
    onCompleted(response) {
      if (response.editAccount) {
        onCompleted();
        onClose();
        clearFields();
      }
    },
  });

  const { successToast } = useToast();

  const surveyTypeOptions = useQueryDictionary({ dictionary: "SurveyTypes" });

  const [name, setName] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [country, setCountry] = useState<string>(me?.id?.substring(0, 2) || "");
  const [state, setState] = useState({ value: "", label: "" });
  const [city, setCity] = useState<string>("");
  const [trial, setTrial] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [disablePasswordLogin, setDisablePasswordLogin] = useState(false);
  const [dataCollections, setDataCollections] = useState<string[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);

  const [error, setError] = useState(emptyErrors);
  const [beError, setBeError] = useState("");
  const isEdit = !!customer;

  useEffect(() => {
    setName(customer?.name ?? "");
    setEndDate(customer?.expiry ? customer.expiry.split(" ")[0] : "");
    setTrial(customer?.trial ?? false);
    setIsDemo(customer?.isDemo ?? false);
    setDisablePasswordLogin(customer?.disabledPwLogin ?? false);

    if (customer?.country) {
      setCountry(customer?.country ?? "");
      const countryName = countriesOptions
        .filter((e) => e.value === customer?.country)
        .map((e) => e.label)[0];
      setStateOptions(states2[countryName]);
      setState(
        customer?.state
          ? states2[countryName].filter(
              (e: any) => e.value === customer.state,
            )[0]
          : { value: "", label: "" },
      );
    } else if (me?.id?.substring(0, 2)) {
      setCountry(me?.id?.substring(0, 2));
      const countryName = countriesOptions
        .filter((e) => e.value === me?.id?.substring(0, 2))
        .map((e) => e.label)[0];
      setStateOptions(states2[countryName]);
    }
    setCity(customer?.city ?? "");
    setDataCollections(
      customer?.dataCollections?.map((s) => s?.id || "").filter((d) => !!d) ??
        [],
    );
  }, [customer, me?.id]);

  useEffect(() => {
    if (open && !country && !customer && me?.id?.substring(0, 2)) {
      setCountry(me?.id?.substring(0, 2));
      const countryName = countriesOptions
        .filter((e) => e.value === me?.id?.substring(0, 2))
        .map((e) => e.label)[0];
      setStateOptions(states2[countryName]);
    }

    if (open && !endDate && customer?.expiry) {
      const expireDate = parseInt(
        customer?.expiry ? customer.expiry.split(" ")[0] : "",
      );
      if (expireDate) {
        setEndDate(formatDate(expireDate, "yyyy-MM-dd"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, me?.id, open]);

  useEffect(() => {
    if (!open || customer || !surveyTypeOptions?.length) return;
    setDataCollections(surveyTypeOptions.map((type) => type.value));
  }, [open, customer, surveyTypeOptions]);
  const handleChangeText =
    (setter: Dispatch<SetStateAction<string>>, key: keyof typeof emptyErrors) =>
    (value: TextInputValue) => {
      setter(value as string);
      setError({
        ...error,
        [key]: "",
      });
    };

  const handleChangeCountry = (option: SelectOption) => {
    setCountry(option.value);
    setStateOptions(states2[option.label]);
    setState({ value: "", label: "" });
    setError({
      ...error,
      country: "",
    });
  };

  const handleChangeState = (option: any) => {
    setState(option);
    setError({
      ...error,
      state: "",
    });
  };

  const handleCreateState = (option: string) => {
    const newOption = { value: option, label: option };
    setStateOptions([...stateOptions, newOption]);
    setState(newOption);
    setError({
      ...error,
      state: "",
    });
  };

  const clearFields = () => {
    setName("");
    setEndDate("");
    setCountry("");
    setStateOptions([]);
    setState({ value: "", label: "" });
    setCity("");
    setTrial(false);
    setBeError("");
    setError(emptyErrors);
    setDataCollections([]);
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const validate = () => {
    const errors: any = {};

    if (validateIsEmpty(name)) {
      errors.name = "Name must not be empty.";
    }

    // ToDo: disable endDate mandatary temporarily
    // if (validateIsEmpty(endDate)) {
    //   errors.endDate = 'End date is required.';
    // }

    if (validateIsEmpty(country)) {
      errors.country = "Country is required";
    }

    if (!state || validateIsEmpty(state.value)) {
      errors.state = "State/Province is required";
    }

    if (validateIsEmpty(city)) {
      errors.city = "City is required";
    }

    return errors;
  };

  const handleEdit = (e: FormEvent) => {
    e.preventDefault();

    const errors = validate();

    if (isEmptyObject(errors)) {
      setBeError("");
      updateAccount({
        variables: {
          input: {
            id: customer!.id,
            name,
            trial,
            isDemo,
            country,
            state: state.value,
            city,
            disabledPwLogin: disablePasswordLogin,
            dataCollections,
            endDate: new Date(endDate).valueOf(),
          },
        },
      });
    } else {
      logger.debug(`[e]`, errors);
      setError(errors);
    }
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();

    const errors = validate();

    if (isEmptyObject(errors)) {
      setBeError("");
      createAccount({
        variables: {
          input: {
            name,
            trial,
            isDemo,
            country,
            state: state.value,
            city,
            dataCollections,
            endDate: new Date(endDate).valueOf(),
          },
        },
      });
    } else {
      logger.debug(`[e]`, errors);
      setError(errors);
    }
  };

  const handleChangeOptions =
    (setter: Dispatch<any>, key?: keyof typeof emptyErrors) => (value: any) => {
      if (key) {
        setError({
          ...error,
          [key]: "",
        });
      }
      setter(value);
    };

  const handleChangeTrial = (value: boolean) => setTrial(value);

  const isActive = useMemo(() => {
    if (!customer) return false;
    const now = new Date().valueOf();
    if (!customer.expiry || Number(customer.expiry) > now) {
      return true;
    }
    return false;
  }, [customer]);

  const copyRegistrationLink = useCallback(async () => {
    await navigator.clipboard.writeText(
      `${window.location.protocol}//${window.location.host}/registration?id=${customer?.id}&entity=acc`,
    );
    successToast("Link copied successfully!");
  }, [customer?.id, successToast]);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={styles.title}>
        {isEdit ? "Edit Account" : "Add Account"}
      </div>
      <form className={styles.form} onSubmit={isEdit ? handleEdit : handleSave}>
        <div className={styles.content}>
          <TextInput
            hasError={!!error.name}
            subTitle={error.name}
            placeholder={"Name"}
            inline={true}
            label={"Name"}
            id={"name"}
            value={name}
            onChange={handleChangeText(setName, "name")}
          />
          <TextInput
            hasError={!!error.endDate}
            subTitle={error.endDate}
            inline={true}
            label={"End Date"}
            id={"endDate"}
            value={endDate}
            onChange={handleChangeText(setEndDate, "endDate")}
            type={"date"}
            min={today}
          />
          <Select
            showSearch={false}
            inline={true}
            value={country}
            onChange={handleChangeCountry}
            placeholder={"Country"}
            subTitle={error.country}
            error={!!error.country}
            label={"Country"}
            options={countriesOptions}
          />
          <SelectCreatable
            inline={true}
            value={state}
            label={"State/Province"}
            options={stateOptions}
            placeholder={"State"}
            onChange={handleChangeState}
            onCreateOption={handleCreateState}
            error={!!error.state}
            subTitle={error.state}
          />
          <TextInput
            hasError={!!error.city}
            subTitle={error.city}
            placeholder={"City"}
            inline={true}
            label={"City"}
            id={"city"}
            value={city}
            onChange={handleChangeText(setCity, "city")}
          />
          <SelectMulti
            label={"Data collection"}
            showSearch={false}
            options={surveyTypeOptions}
            onChange={handleChangeOptions(setDataCollections)}
            value={dataCollections}
            inline={true}
          />
          {isEdit ? (
            <>
              <div className={styles.courseAction}>
                <div className={styles.headerLabel}>Status</div>
                <div>
                  <div
                    className={`${styles.status} ${isActive ? styles.activeStatus : ""}`}
                  >
                    {isActive ? "Active" : "Expired"}
                  </div>
                </div>
              </div>
            </>
          ) : null}
          <div className={styles.row}>
            <div />
            <Checkbox
              id={"trial"}
              checked={trial}
              onChange={handleChangeTrial}
              label={"Trial"}
            />
          </div>
          <div className={styles.row}>
            <div />
            <Checkbox
              id={"isDemo"}
              checked={isDemo}
              onChange={(v) => setIsDemo(v)}
              label={"Demo (users only see the Programs page)"}
            />
          </div>
          {isEdit && (
            <div className={styles.row}>
              <div />
              <Checkbox
                id={"disablePasswordLogin"}
                checked={disablePasswordLogin}
                onChange={(v) => setDisablePasswordLogin(v)}
                label={"Disable Email/Password Login"}
              />
            </div>
          )}

          {isEdit ? (
            <>
              <div className={styles.action}>
                <div />
                <div>
                  <Button
                    disabled={editLoading}
                    label={"Copy registration link "}
                    outline
                    className={styles.copyButton}
                    onClick={copyRegistrationLink}
                  />
                </div>
              </div>
            </>
          ) : null}
          {Boolean(beError) && <div className={styles.error}>{beError}</div>}
          <div className={styles.action}>
            <div />
            {isEdit ? (
              <Button disabled={editLoading} type={"submit"} label={"Save"} />
            ) : (
              <Button disabled={loading} type={"submit"} label={"Create"} />
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default AddModal;
